export type LensId = "operations" | "mobility" | "environment" | "safety" | "flood" | "vibes" | "executive" | "energy" | "amenities" | "control" | "latkrabang";

// ── Base maps ──────────────────────────────────────────────────────────
// Mutually exclusive ground layer that everything else stacks on top of.
// Picking one swaps the bottom imagery: streets (CARTO vector tiles),
// topography (OpenTopoMap contours), aerial (Esri high-res satellite),
// night (VIIRS DayNightBand), or true-color (VIIRS NOAA-20). The picker
// lives at the very top of LayerPalette so the user resolves the "what
// am I looking at?" question first, then layers facts on top.
export type BaseMapId = "streets" | "topo" | "aerial" | "night" | "true-color";

export interface BaseMap {
  id: BaseMapId;
  label: string;
  describe: string;
  /** Tiny color chip on the picker button so users associate map style with hue. */
  swatch: string;
}

export const BASE_MAPS: BaseMap[] = [
  { id: "streets",    label: "Streets",    describe: "CARTO vector tiles — clean grey-scale streets + labels. Default for vector overlays.",      swatch: "#3F4756" },
  { id: "topo",       label: "Topo",       describe: "OpenTopoMap — contours, hillshade, terrain. Useful at regional zoom.",                       swatch: "#A3E635" },
  { id: "aerial",     label: "Aerial",     describe: "Esri World Imagery — high-resolution aerial / satellite photos. Best for ground-truth.",     swatch: "#60A5FA" },
  { id: "night",      label: "Night",      describe: "VIIRS DayNightBand — Earth at night. Bangkok shows up as a golden constellation.",            swatch: "#FACC15" },
  { id: "true-color", label: "True color", describe: "VIIRS NOAA-20 true-color — daily corrected reflectance, sharp clouds + ground.",              swatch: "#A5F3FC" },
];

export type LayerId =
  | "campus-boundary"
  | "campus-buildings"
  | "google-3d-tiles"
  | "bangkok-districts"
  | "gistda-fire-hotspots"
  | "satellite-viirs-truecolor"
  | "satellite-night"
  | "satellite-imerg"
  | "satellite-aerosol"
  | "satellite-no2"
  | "bma-pois"
  | "bma-parks"
  | "bma-aq-stations"
  | "aq-heatmap"
  | "traffic-heatmap"
  | "incidents-itic"
  | "incidents-city-reports"
  | "incidents-city-heatmap"
  | "cctv-cameras"
  | "transit-stations"
  | "transit-lines"
  | "campus-gates"
  | "road-network"
  | "satellite-ndvi"
  | "satellite-true-color"
  | "satellite-esri"
  | "surrounding-buildings"
  | "google-satellite"
  | "google-traffic"
  | "satellite-lst"
  | "satellite-flood"
  | "gfm-flood-extent"
  | "satellite-himawari"
  | "satellite-terrain"
  | "alphaearth-landcover"
  | "alphaearth-change"
  | "alphaearth-surface"
  | "campus-energy"
  | "campus-trees"
  // Neighborhood POIs (OSM-derived; see adapters/neighborhoodPois.ts)
  | "poi-school"
  | "poi-religious"
  | "poi-hotel"
  | "poi-pharmacy"
  | "poi-cultural"
  | "poi-bus-stop"
  | "poi-pier"
  | "poi-embassy"
  | "poi-ev"
  | "poi-retail"
  | "poi-bank"
  // Lat Krabang district — toggleable layers (OSM boundary + POIs, district stats).
  | "lat-krabang-boundary"
  | "lat-krabang-pois"
  | "lat-krabang-zones"
  // Flood watch + X-RAY subsurface (KMITL-local)
  | "flood-watch-stations"
  | "xray-electricity"
  | "xray-water"
  | "xray-drainage"
  | "xray-rail"
  // Surface waterway network (verbatim OSM) + flow direction (2D)
  // + live HII flood gauges. The hand-drawn flood-prone polygons that used to
  // sit here were removed 2026-10-03 — see floodGaugeLayer for why.
  | "waterways"
  | "waterways-flow"
  | "flood-gauges"
  | "flood-areas";

export interface Lens {
  id: LensId;
  label: string;
  /** Full name + 1-line explanation rendered as the button's hover tooltip. */
  describe: string;
  layers: LayerId[];
  /** Base map that snaps in when this lens is picked. Defaults to "streets". */
  baseMap?: BaseMapId;
}

export const LENSES: Lens[] = [
  {
    id: "executive",
    label: "EXEC",
    describe:
      "Executive — strategic view of the KMITL Lat Krabang campus. Aerial base + campus boundary + buildings + gates + ARL/SRT transit + waterway network + BMA AQ stations. For the rector and leadership team.",
    baseMap: "aerial",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "campus-gates",
      "bangkok-districts",
      "bma-aq-stations",
      "transit-stations",
      "transit-lines",
      "waterways",
      "flood-gauges",
    ],
  },
  {
    id: "operations",
    label: "OPS",
    describe: "Operations — KMITL campus + waterway network + live traffic + citizen/iTIC incidents + HII flood gauges + ARL/SRT transit. Default day-to-day view. Toggle road-network / bma-pois / traffic-heatmap on for extra context.",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "campus-gates",
      "waterways",
      "waterways-flow",
      "flood-gauges",
      "flood-areas",
      "flood-watch-stations",
      "incidents-city-reports",
      "incidents-itic",
      "transit-stations",
      "transit-lines",
      "bma-aq-stations",
      "lat-krabang-boundary",
    ],
  },
  {
    id: "mobility",
    label: "MOB",
    describe: "Mobility — KMITL road network, ARL Lat Krabang + SRT Eastern line tracks + stations, traffic heatmap, iTIC events, campus gates, and Longdo CCTV. For dispatch + routing around Chalong Krung and Motorway No.7.",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "road-network",
      "transit-lines",
      "transit-stations",
      "campus-gates",
      "traffic-heatmap",
      "incidents-itic",
      "cctv-cameras",
    ],
  },
  {
    id: "environment",
    label: "ENV",
    describe: "Environment — aerial base + canopy trees, BMA green spaces, AQ + PM2.5 heatmap, HII flood/rain gauges, AlphaEarth land cover. NDVI/LST/NO2 are opt-in for regional zoom.",
    baseMap: "aerial",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "bma-parks",
      "bma-aq-stations",
      "aq-heatmap",
      "flood-watch-stations",
      "waterways",
      "waterways-flow",
      "flood-gauges",
      "campus-trees",
      "alphaearth-landcover",
      "alphaearth-surface",
    ],
  },
  {
    id: "safety",
    label: "SAF",
    describe: "Safety — citizen reports (Traffy / CR), iTIC traffic events, BMA hospitals + fire + police POIs, CCTV, HII ThaiWater flood watch gauges, plus MODIS flood + VIIRS fire detection when available.",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "incidents-city-reports",
      "incidents-city-heatmap",
      "incidents-itic",
      "flood-watch-stations",
      "waterways",
      "waterways-flow",
      "flood-gauges",
      "gistda-fire-hotspots",
      "satellite-flood",
      "cctv-cameras",
      "bma-pois",
    ],
  },
  {
    id: "flood",
    label: "FLD",
    describe:
      "Flood focus — the eastern basin under live observation. Copernicus GFM observed flood extent (Sentinel-1 radar, 20 m) drawn on the ground so you can zoom into the water itself, real OpenStreetMap geometry for every khlong and drain, and the live HII gauge network showing situation level and metres to bank. Built for an active monsoon event: the gauge layer shows what is actually over its bank right now, not a drawn-up risk zone.",
    baseMap: "aerial",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "campus-gates",
      "waterways",
      "waterways-flow",
      "flood-gauges",
      "flood-watch-stations",
      "transit-lines",
      "transit-stations",
      "gfm-flood-extent",
      "satellite-flood",
      "lat-krabang-boundary",
    ],
  },
  {
    id: "vibes",
    label: "VIB",
    describe: "Vibes — where campus life happens. KMITL campus over an aerial base, BMA markets + malls + parks, the road grid, retail and hotels in the Lat Krabang dormitory belt around the institute.",
    baseMap: "aerial",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "bma-pois",
      "bma-parks",
      "road-network",
      "poi-retail",
      "poi-hotel",
    ],
  },
  {
    id: "energy",
    label: "EGY",
    describe: "Energy — KMITL campus buildings recolored by current kWh draw (synthetic SCENARIO model, stand-in for BEMS until the partnership lands). Stuck-AC anomalies flag red. Combine with 3D to extrude buildings by height while colored by load.",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "campus-energy",
      "road-network",
    ],
  },
  {
    id: "amenities",
    label: "AMN",
    describe: "Amenities — what's around the KMITL campus for everyone. Schools, hospitals (BMA POIs), religious sites, cultural venues, pharmacies, EV charging across Lat Krabang. The transparency-friendly view of neighborhood services near Suvarnabhumi.",
    baseMap: "aerial",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "road-network",
      "bma-pois",
      "poi-school",
      "poi-religious",
      "poi-cultural",
      "poi-pharmacy",
      "poi-ev",
      "poi-retail",
    ],
  },
  {
    id: "control",
    label: "CTRL",
    describe: "God's Mode — toggle gates, lights, AC, pumps and alarms by zone. Venue occupancy, parking availability, shared bikes, and transit departures. For facilities management and the duty manager.",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "campus-gates",
      "road-network",
      "cctv-cameras",
    ],
  },
  {
    id: "latkrabang",
    label: "LATKRABANG",
    describe:
      "Lat Krabang district — full 12×12 km polygon plus all OSM-tagged hospitals, schools, universities, religious places, community centres, and fire/police stations within the district. Pairs with the Lat Krabang stats panel (population, businesses, risks, religion, schools, solar). For the district officer and any external stakeholder that wants to see KMITL in its neighbourhood context.",
    baseMap: "streets",
    layers: [
      "lat-krabang-boundary",
      "lat-krabang-pois",
      "lat-krabang-zones",
      "campus-boundary",
      "campus-buildings",
      "surrounding-buildings",
      "campus-gates",
      "bangkok-districts",
      "transit-stations",
      "transit-lines",
      "road-network",
    ],
  },
];

export type LayerGroup = "campus" | "mobility" | "incidents" | "utility" | "imagery" | "environment" | "neighborhood" | "xray" | "district";

export const LAYER_GROUP_LABEL: Record<LayerGroup, string> = {
  campus:    "Campus",
  mobility:  "Mobility",
  incidents: "Incidents",
  utility:   "Energy",
  imagery:   "Imagery · αEarth + Satellites",
  environment: "Environment",
  neighborhood: "Neighborhood",
  xray:      "X-Ray · Underground",
  district:  "Lat Krabang",
};

export const ALL_LAYERS: {
  id: LayerId;
  label: string;
  swatch: string;
  group: LayerGroup;
  /** Hover tooltip text — what this layer shows and where the data comes from. */
  describe: string;
}[] = [
  // Campus
  { id: "campus-boundary",   label: "Campus boundary",         swatch: "#B7307A", group: "campus",
    describe: "KMITL Lat Krabang official perimeter from OSM." },
  { id: "campus-buildings",  label: "Campus buildings (OSM)",  swatch: "#B7307A", group: "campus",
    describe: "KMITL campus building footprints, with height from OSM building:levels (or our heuristic). Click any building for name + levels; 3D mode extrudes them." },
  { id: "bma-pois",          label: "BMA POIs (hosp/fire/…)",  swatch: "#EF4444", group: "campus",
    describe: "BMA points-of-interest in the campus bbox: hospitals, fire stations, police, health centers, BMA offices, markets. From bmagis.bangkok.go.th." },
  { id: "bma-parks",         label: "BMA parks / green space", swatch: "#34D399", group: "campus",
    describe: "196 named green spaces from the BMA Public Park dataset — includes rooftop gardens, pocket parks, and expressway green strips around campus." },
  { id: "bma-aq-stations",   label: "AQ stations",             swatch: "#22D3EE", group: "campus",
    describe: "Live air-quality station dots — color-coded by PM2.5 reading (green→yellow→orange→red→purple). Pulls from BMA + AQICN (PCD, US-embassy, research monitors when AQICN_TOKEN is set)." },
  { id: "aq-heatmap",        label: "PM2.5 heatmap (IQAir)",  swatch: "#FF7E00", group: "campus",
    describe: "IQAir-style interpolated PM2.5 surface. AQI color stops: green (Good) → yellow (Moderate) → orange (USG) → red (Unhealthy) → purple (Very Unhealthy) → maroon (Hazardous). Densifies as more stations come online." },
  // Mobility
  { id: "traffic-heatmap",   label: "Traffic — by hour",       swatch: "#F87171", group: "mobility",
    describe: "Modelled traffic intensity along OSM road centrelines, weighted by hour-of-day + weekday/weekend. Drives the orange/red glow on arterials. Will accept live probe data when wired." },
  { id: "transit-stations",  label: "ARL / SRT stations",      swatch: "#38BDF8", group: "mobility",
    describe: "Rail stations serving Lat Krabang: Airport Rail Link Lat Krabang and the SRT Eastern Line stops. Walking / shuttle distance to campus gates." },
  { id: "transit-lines",     label: "ARL / SRT lines",         swatch: "#057B43", group: "mobility",
    describe: "Polyline tracks for the Airport Rail Link and the SRT Eastern line through Lat Krabang. Coloured per system." },
  { id: "road-network",      label: "Road network (classified)", swatch: "#FB7185", group: "mobility",
    describe: "OSM road network coloured + scaled by class: motorway/primary thick warm (Motorway No.7, Chalong Krung), secondary/tertiary medium cyan, residential/lane thin neutral. Drives orientation around the campus." },
  { id: "campus-gates",      label: "Campus gates / entrances", swatch: "#FBBF24", group: "campus",
    describe: "Every gate and entrance on the KMITL campus perimeter. Named gates render larger; minor gates smaller." },
  { id: "cctv-cameras",      label: "CCTV cameras",            swatch: "#E5E7EB", group: "mobility",
    describe: "Public traffic cameras from Longdo (BMA + other operators) inside the campus bbox. Click for the live JPG/HLS stream." },
  // Incidents
  { id: "incidents-itic",    label: "iTIC traffic events",     swatch: "#F59E0B", group: "incidents",
    describe: "Live iTIC / Longdo traffic events: accidents, closures, breakdowns, construction. Filtered to campus bbox." },
  { id: "incidents-city-reports", label: "City Reporter — dots",   swatch: "#A78BFA", group: "incidents",
    describe: "Live citizen complaints (LINE / Telegram / Traffy): road damage, lighting, drainage, waste, etc. Color = category, size = severity. Click any dot for the full audit trail." },
  { id: "incidents-city-heatmap", label: "City Reporter — hotspots", swatch: "#F87171", group: "incidents",
    describe: "Severity-weighted density heatmap of open city reports. Highlights recurring problem zones — a single critical report still shows as a hot spot. Resolved reports are excluded." },
  // Imagery (atmospheric / data overlays — base maps now live in BASE_MAPS picker)
  { id: "alphaearth-landcover", label: "αEarth land cover (10 m)", swatch: "#228B22", group: "imagery",
    describe: "AlphaEarth Foundations AI-classified land cover: built-up, tree canopy, grass, water, bare soil, road. Derived from 64-dim satellite embeddings (GOOGLE/SATELLITE_EMBEDDING/V1/ANNUAL) via unsupervised k-means. 10 m resolution, annual." },
  { id: "alphaearth-change",  label: "αEarth change detection",   swatch: "#F44336", group: "imagery",
    describe: "AlphaEarth year-over-year change detection: cosine distance between annual embeddings highlights new construction, tree loss, and surface modifications. Brighter = more change." },
  { id: "alphaearth-surface", label: "αEarth surface greenness",  swatch: "#8BC34A", group: "imagery",
    describe: "AlphaEarth surface greenness proxy derived from embedding projection. Brown = built-up / impervious, green = vegetation. Complements NDVI with multi-sensor (SAR + LiDAR + climate) integration." },
  { id: "satellite-himawari", label: "Himawari IR (regional)",   swatch: "#C7D2FE", group: "imagery",
    describe: "Himawari-9 Band 13 infrared — geostationary cloud loop refreshed every 10 min. Best for catching incoming storm fronts. Regional zoom only." },
  { id: "satellite-imerg",   label: "IMERG rainfall (live)",       swatch: "#06B6D4", group: "imagery",
    describe: "NASA IMERG global precipitation rate — half-hourly, color-coded rainfall intensity. Watch monsoon cells move across the Gulf of Thailand." },
  { id: "satellite-ndvi",    label: "NDVI greenery (regional)", swatch: "#34D399", group: "imagery",
    describe: "MODIS NDVI 8-day composite — vegetation greenness. Useful for tracking campus + park canopy over time. Regional zoom." },
  { id: "satellite-lst",     label: "Land surface temp (regional)", swatch: "#F97316", group: "imagery",
    describe: "MODIS Land Surface Temperature (day) — shows urban heat islands. Bangkok core typically reads 4–7 °C hotter than surrounding fields." },
  { id: "satellite-aerosol", label: "Aerosol optical depth",       swatch: "#F472B6", group: "imagery",
    describe: "MODIS MAIAC AOD — direct satellite proxy for PM2.5 + smoke / haze layers. Lights up Bangkok during burning season + dust events." },
  { id: "satellite-no2",     label: "NO₂ pollution (OMI)",         swatch: "#EF4444", group: "imagery",
    describe: "OMI tropospheric NO₂ — atmospheric nitrogen dioxide from traffic + power plants. Traffic corridors light up over Bangkok arterials." },
  { id: "gfm-flood-extent", label: "Observed flood extent (Sentinel-1)", swatch: "#D81B60", group: "imagery",
    describe: "Copernicus GFM flood extent — Sentinel-1 radar, three detection algorithms combined by majority vote, 20 m. Where the water actually is on the ground, on the date of the last pass that saw any. Goes through cloud and at night." },
  { id: "satellite-flood",   label: "Flood detection (regional)",   swatch: "#38BDF8", group: "imagery",
    describe: "MODIS 3-day combined flood detection — water surfaces flagged by satellite. Critical during monsoon flooding of Klong Saen Saep system." },
  { id: "surrounding-buildings", label: "Building footprints (1 km)", swatch: "#7A94C0", group: "campus",
    describe: "Every building footprint within 1 km of KMITL from Overture Maps — real outlines, fused from Google Open Buildings, Microsoft and OpenStreetMap. Drawn flat: only 20 of 1,790 carry a height, so the rest are shown as footprint-known, height-unknown rather than given an invented one." },
  { id: "google-3d-tiles", label: "Google 3D Tiles", swatch: "#60A5FA", group: "imagery",
    describe: "Photorealistic 3D city model using Google Maps 3D Tiles API (requires API key). Disables default blocky extrusions." },
  { id: "google-satellite",  label: "Satellite (Google HD)",    swatch: "#34D399", group: "imagery",
    describe: "Google Maps satellite imagery via the Map Tiles API — often more recent and higher-res than Esri. Requires a Google Maps key." },
  { id: "google-traffic",    label: "Live Traffic (Google)",    swatch: "#F87171", group: "imagery",
    describe: "Google real-time traffic overlay (Map Tiles API). Dense over Bangkok and major corridors; provincial roads may show little flow data. Requires a Google Maps key." },
  // Environment / Risk
  { id: "bangkok-districts", label: "Bangkok district lines",      swatch: "#FFFFFF", group: "environment",
    describe: "Approximate boundaries of Bangkok districts around campus, centred on Lat Krabang and the eastern fringe toward Suvarnabhumi." },
  { id: "gistda-fire-hotspots", label: "VIIRS fire hotspots (GISTDA)", swatch: "#FB923C", group: "environment",
    describe: "GISTDA VIIRS active fire hotspots — NOAA-20 satellite passes, color-coded by confidence (high=red, nominal=orange, low=amber). Brightness > 400 K = confirmed active fire. Updates when GISTDA API key is available." },
  // Energy
  { id: "campus-energy", label: "Energy heatmap (scenario)",  swatch: "#F0C850", group: "utility",
    describe: "Recolor the KMITL campus building footprints by current kWh draw. Cool teal = idle, yellow = peak, red = anomaly (stuck cooling, after-hours load, possible outage). Synthetic SCENARIO model derived from building geometry + hour-of-day until BEMS is wired." },
  // Trees + canopy
  { id: "campus-trees", label: "Campus trees",  swatch: "#2A9859", group: "environment",
    describe: "Documented trees inside the KMITL campus bbox from OSM, each rendered as a canopy disk at its real crown radius. Compare against the satellite to verify or flag missing coverage." },
  // Neighborhood POIs (OSM Overpass; ~12h cache. See adapters/neighborhoodPois.ts)
  { id: "poi-school",    label: "Schools + Universities", swatch: "#60A5FA", group: "neighborhood",
    describe: "Schools, universities, colleges, kindergartens, language schools from OSM around Lat Krabang." },
  { id: "poi-religious", label: "Temples + Shrines",      swatch: "#FBBF24", group: "neighborhood",
    describe: "Religious sites from OSM in the Lat Krabang campus bbox." },
  { id: "poi-hotel",     label: "Hotels + Hostels",       swatch: "#C084FC", group: "neighborhood",
    describe: "Hotels, hostels and guest-houses around the KMITL campus and Suvarnabhumi corridor." },
  { id: "poi-pharmacy",  label: "Pharmacies",             swatch: "#34D399", group: "neighborhood",
    describe: "Pharmacies near campus — Watsons, Boots, Fascino, plus standalone chemists." },
  { id: "poi-cultural",  label: "Cultural venues",        swatch: "#F472B6", group: "neighborhood",
    describe: "Theatres, cinemas, arts centres, libraries and community centres around Lat Krabang." },
  { id: "poi-bus-stop",  label: "Bus stops (named)",      swatch: "#A3E635", group: "neighborhood",
    describe: "Named bus stops from OSM. Unnamed pole-only stops are filtered out so we don't paint the arterials with anonymous dots." },
  { id: "poi-pier",      label: "Canal piers",            swatch: "#38BDF8", group: "neighborhood",
    describe: "Boat-service piers on the canals around Lat Krabang from OSM." },
  { id: "poi-embassy",   label: "Embassies + Consulates", swatch: "#EF4444", group: "neighborhood",
    describe: "Diplomatic offices in or near the campus bbox from OSM." },
  { id: "poi-ev",        label: "EV charging",            swatch: "#10B981", group: "neighborhood",
    describe: "Public EV charging stations from OSM — useful as the KMITL fleet electrifies." },
  { id: "poi-retail",    label: "Retail (7-11/mall/super)", swatch: "#FB923C", group: "neighborhood",
    describe: "Convenience stores (mostly 7-Eleven + FamilyMart), supermarkets and malls. The everyday-needs layer for the dormitory belt." },
  { id: "poi-bank",      label: "Banks + ATMs",           swatch: "#FACC15", group: "neighborhood",
    describe: "Bank branches and standalone ATMs from OSM. Default off — useful for visitor wayfinding." },

  // Lat Krabang district — togglable boundary + POI dots for the entire
  // เขตลาดกระบัง khet. Pairs with the Lat Krabang stats panel which
  // renders the data.go.th non-geospatial datasets as charts.
  { id: "lat-krabang-boundary", label: "Lat Krabang boundary", swatch: "#B7307A", group: "district",
    describe: "Full Lat Krabang district polygon from OSM admin_level=6 (relation 3147500). Toggle on to see how KMITL sits inside the 12×12 km khet; combines well with lat-krabang-pois for neighbourhood context." },
  { id: "lat-krabang-pois",     label: "District POIs (OSM)",   swatch: "#38BDF8", group: "district",
    describe: "OpenStreetMap hospitals, schools, universities, religious places, community centres, and fire/police stations WITHIN the Lat Krabang boundary. Source: Overpass area query against relation 3147500." },
  { id: "lat-krabang-zones",    label: "Subdistrict zones (color-coded)", swatch: "#FACC15", group: "district",
    describe: "Polygon per subdistrict (khwaeng) inside Lat Krabang, fill-coloured by the chosen metric (population · businesses · risk points · students). Switch the metric with the pill buttons next to the toggle — the ramp re-buckets immediately. Joins OSM admin_level=7 polygons with data.go.th stats by Thai name." },

  // Flood watch (HII ThaiWater)
  { id: "flood-watch-stations", label: "HII flood / rain gauges", swatch: "#0EA5E9", group: "environment",
    describe: "Live Hydro-Informatics Institute water-level + 24h rain gauges near KMITL (FloodDash watch feed). Colour = situation level (WL) or rain intensity. Click a station name in the FLOOD WATCH panel to fly there." },

  // Surface waterway network (2D) — promoted out of 3DS X-RAY so the
  // operator can see khlongs, canals and campus drains in any view mode.
  // Pairs with the flow-direction arrows for an at-a-glance read of where
  // the water is going.
  { id: "waterways", label: "Waterway network (khlongs + drains)", swatch: "#38BDF8", group: "environment",
    describe: "Lat Krabang khlongs (Khlong Prawet Burirom, Lat Krabang, Chorakhe Yai, Hua Takhe) plus campus stormwater drains. Line width ∝ channel width (1–22 m). Coordinates ordered upstream→downstream so the flow arrows on `Waterways → flow` line up with the channels." },
  { id: "waterways-flow", label: "Flow direction arrows", swatch: "#0EA5E9", group: "environment",
    describe: "Canvas-rendered triangles at the 60% mark of each khlong, rotated to the line's bearing. Shows which way the water flows — Prawet Burirom → west, Lat Krabang → south into Chorakhe Yai, etc. Enable with `Waterway network` to read the basin." },
  { id: "flood-gauges", label: "HII flood gauges (live)", swatch: "#EF4444", group: "environment",
    describe: "Every live HII river/canal gauge FloodDash is tracking within 30 km of campus — real station coordinates, situation level 1–5, water level in m MSL, and metres remaining to the bank. Teal = normal, amber = watch, orange = prepare, red = over bank. This layer replaced five hand-drawn flood-prone rectangles on 2026-10-03; a gauge is a measurement, and it disappears when the water goes down." },

  { id: "flood-areas", label: "Flood areas (reported)", swatch: "#F8FAFC", group: "environment",
    describe: "Named places FloodDash has matched to flood coverage — districts, tambons, landmarks — joined against HII sensor evidence into a tier. White = confirmed (a headline names this place AND a sensor agrees). Orange = reported (coverage only). Grey = sensor only (instrument evidence, no coverage names the place). Separate from the gauge layer on purpose: a place can be well-evidenced and only moderately flooded, and the two answers are different questions." },

  // X-RAY subsurface — shown when view mode is 3DS (and toggleable in palette)
  { id: "xray-electricity", label: "Electricity corridors (−2 m)", swatch: "#F59E0B", group: "xray",
    describe: "Reference MEA-style power corridors along major Lat Krabang roads at −2 m burial. Not as-built CAD — corridor approximation until MEA GIS is licensed." },
  { id: "xray-water",       label: "Water mains (−3 m)",           swatch: "#38BDF8", group: "xray",
    describe: "Reference MWA-style water mains along major roads at −3 m. Corridor approximation for campus X-RAY view." },
  { id: "xray-drainage",    label: "Drainage / khlong (−4 m)",     swatch: "#10B981", group: "xray",
    describe: "Khlong Prawet Burirom + campus drain centerlines at −4 m. Use with FLOOD WATCH gauges on SAF/ENV lenses." },
  { id: "xray-rail",        label: "SRT / ARL corridor",           swatch: "#6E4B3A", group: "xray",
    describe: "Airport Rail Link + SRT Eastern Line tracks through Lat Krabang (grade). Anchors the underground view to real transit." },
];
